import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { explainFunctionError, functionErrorResponse, useSupabase, useTenant } from "@how/app-kit";
import type { Enums } from "@how/database";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type EventVisibility = Enums<"event_visibility">;
export type RegistrationRule = Enums<"event_registration_rule">;
export type RegistrationStatus = Enums<"event_registration_status">;

export const VISIBILITY_LABEL: Record<EventVisibility, string> = {
  public: "Public",
  unlisted: "Unlisted",
  private: "Private",
};
export const REGISTRATION_LABEL: Record<RegistrationRule, string> = {
  none: "Just come",
  open: "Register",
  closed: "Registration closed",
};
export const STATUS_LABEL: Record<RegistrationStatus, string> = {
  registered: "Registered",
  waitlisted: "Waiting list",
  cancelled: "Cancelled",
  attended: "Attended",
};

/** A date and time in the reader's locale; the occurrence is a wall-clock time at the campus (ADR 0036). */
export function formatWhen(iso: string, timeZone?: string | null): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

export interface EventCategory {
  readonly id: string;
  readonly name: string;
  readonly sortOrder: number;
}

export function useEventCategories() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["event-categories", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<EventCategory[]> => {
      const { data, error } = await supabase
        .from("event_categories")
        .select("id, name, sort_order")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((c) => ({ id: c.id, name: c.name, sortOrder: c.sort_order }));
    },
  });
}

export interface EventRow {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly description: string | null;
  readonly categoryId: string | null;
  readonly categoryName: string | null;
  readonly campusId: string | null;
  readonly campusName: string | null;
  readonly campusTimeZone: string | null;
  readonly ministry: string | null;
  readonly location: string | null;
  readonly visibility: EventVisibility;
  readonly registration: RegistrationRule;
  readonly capacity: number | null;
  readonly priceCents: number | null;
  readonly isActive: boolean;
  /** The next occurrence from now, if any. */
  readonly nextAt: string | null;
  readonly upcomingCount: number;
}

export interface EventsQuery {
  readonly search: string;
  readonly categoryId?: string;
  readonly campusId?: string;
  readonly status: "active" | "archived" | "all";
  /** "upcoming" keeps events with a future occurrence; "all" shows every event. */
  readonly when: "upcoming" | "all";
}

/** Every event with its next date and how many dates are ahead. A church has tens, so the list is read whole. */
export function useEvents(q: EventsQuery) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["events", tenantId, q],
    enabled: tenantId !== null,
    queryFn: async (): Promise<EventRow[]> => {
      let query = supabase
        .from("events")
        .select(
          "id, title, slug, description, category_id, campus_id, ministry, location, visibility, registration, capacity, price_cents, is_active, event_categories(name), campuses(name, time_zone), event_occurrences(starts_at, is_cancelled)",
        )
        .eq("tenant_id", tenantId ?? "")
        .order("title");
      if (q.status !== "all") query = query.eq("is_active", q.status === "active");
      if (q.categoryId !== undefined) query = query.eq("category_id", q.categoryId);
      if (q.campusId !== undefined) query = query.eq("campus_id", q.campusId);
      const trimmed = q.search.trim().replace(/[%_]/g, "");
      if (trimmed.length > 0) query = query.ilike("title", `%${trimmed}%`);
      const { data, error } = await query;
      if (error) throw error;
      const now = Date.now();
      const rows = data.map((e) => {
        const upcoming = e.event_occurrences
          .filter((o) => !o.is_cancelled && new Date(o.starts_at).getTime() >= now)
          .map((o) => o.starts_at)
          .sort();
        return {
          id: e.id,
          title: e.title,
          slug: e.slug,
          description: e.description,
          categoryId: e.category_id,
          categoryName: e.event_categories?.name ?? null,
          campusId: e.campus_id,
          campusName: e.campuses?.name ?? null,
          campusTimeZone: e.campuses?.time_zone ?? null,
          ministry: e.ministry,
          location: e.location,
          visibility: e.visibility,
          registration: e.registration,
          capacity: e.capacity,
          priceCents: e.price_cents,
          isActive: e.is_active,
          nextAt: upcoming[0] ?? null,
          upcomingCount: upcoming.length,
        };
      });
      const kept = q.when === "upcoming" ? rows.filter((r) => r.nextAt !== null) : rows;
      // Soonest first; events with nothing ahead last, by title.
      return kept.sort((a, b) =>
        a.nextAt === null && b.nextAt === null
          ? a.title.localeCompare(b.title)
          : a.nextAt === null
            ? 1
            : b.nextAt === null
              ? -1
              : a.nextAt.localeCompare(b.nextAt),
      );
    },
  });
}

export interface Occurrence {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly note: string | null;
  readonly isCancelled: boolean;
  readonly registered: number;
  readonly waitlisted: number;
  readonly attended: number;
}

export function useOccurrences(eventId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["event-occurrences", tenantId, eventId],
    enabled: eventId !== null,
    queryFn: async (): Promise<Occurrence[]> => {
      const { data, error } = await supabase
        .from("event_occurrences")
        .select("id, starts_at, ends_at, note, is_cancelled, event_registrations(status)")
        .eq("event_id", eventId ?? "")
        .order("starts_at");
      if (error) throw error;
      return data.map((o) => ({
        id: o.id,
        startsAt: o.starts_at,
        endsAt: o.ends_at,
        note: o.note,
        isCancelled: o.is_cancelled,
        registered: o.event_registrations.filter((r) => r.status === "registered").length,
        waitlisted: o.event_registrations.filter((r) => r.status === "waitlisted").length,
        attended: o.event_registrations.filter((r) => r.status === "attended").length,
      }));
    },
  });
}

/** One order of add-ons on a registration: handed over at the event once it is paid (#264). */
export interface RegistrationAddOns {
  readonly orderId: string;
  /** The order's number, which staff and the member say to each other (and the undo dialog asks about, #280). */
  readonly number: number;
  readonly status: "pending_payment" | "paid" | "fulfilled" | "cancelled";
  readonly items: string;
}

export interface Registration {
  readonly id: string;
  readonly personId: string | null;
  readonly name: string;
  readonly isGuest: boolean;
  readonly guestEmail: string | null;
  readonly status: RegistrationStatus;
  readonly note: string | null;
  readonly registeredAt: string;
  readonly paymentStatus: "not_required" | "pending" | "paid" | "failed" | "refunded";
  readonly amountCents: number | null;
  readonly currency: string;
  /** How much has gone back; a partial refund leaves the registration paid. */
  readonly refundedCents: number;
  readonly registeredByName: string | null;
  /** The add-ons chosen with this registration and not cancelled (ADR 0047 step 3); empty without commerce.read. */
  readonly addOns: readonly RegistrationAddOns[];
}

/** Cents as money for the screen, in the registration's currency. */
export function formatMoney(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Who is on an occurrence. Person rows come through the policies, so a child's row is absent for a caller without
 * people.read_minors (ADR 0028); the count on the occurrence still includes them.
 */
export function useRegistrations(occurrenceId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["event-registrations", tenantId, occurrenceId],
    enabled: occurrenceId !== null,
    queryFn: async (): Promise<Registration[]> => {
      const { data, error } = await supabase
        .from("event_registrations")
        .select(
          "id, person_id, guest_name, guest_email, status, note, registered_at, payment_status, amount_cents, currency, refunded_cents, people!event_registrations_person_fk(first_name, last_name, preferred_name), registered_by:people!event_registrations_by_fk(first_name, last_name), orders(id, number, status, order_items(product_name, variant_name, quantity, created_at))",
        )
        .eq("occurrence_id", occurrenceId ?? "")
        .order("status")
        .order("registered_at");
      if (error) throw error;
      return data.map((r) => ({
        id: r.id,
        personId: r.person_id,
        name:
          r.people === null
            ? (r.guest_name ?? "Guest")
            : `${r.people.preferred_name ?? r.people.first_name} ${r.people.last_name}`,
        isGuest: r.person_id === null,
        guestEmail: r.guest_email,
        status: r.status,
        note: r.note,
        registeredAt: r.registered_at,
        paymentStatus: r.payment_status,
        amountCents: r.amount_cents,
        currency: r.currency ?? "usd",
        refundedCents: r.refunded_cents,
        registeredByName:
          r.registered_by === null
            ? null
            : `${r.registered_by.first_name} ${r.registered_by.last_name}`,
        addOns: r.orders
          .filter((o) => o.status !== "cancelled")
          .map((o) => ({
            orderId: o.id,
            number: o.number,
            status: o.status,
            items: [...o.order_items]
              .sort((a, b) => a.created_at.localeCompare(b.created_at))
              .map((i) => `${String(i.quantity)} × ${i.product_name} (${i.variant_name})`)
              .join(", "),
          })),
      }));
    },
  });
}

/**
 * Refund a paid registration (ADR 0009): the stripe-refund function applies the church's rules as the caller, makes
 * the refund on the church's own connected account and records it; the roster re-reads.
 */
export function useRefundRegistration() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly registrationId: string;
      readonly amountCents: number;
      readonly reason: string;
    }): Promise<{ status: string; refundedCents: number; remainingCents: number }> => {
      const result = await supabase.functions.invoke<{
        status: string;
        refundedCents: number;
        remainingCents: number;
      }>("stripe-refund", {
        body: {
          tenant_id: tenantId ?? "",
          registration_id: input.registrationId,
          amount_cents: input.amountCents,
          ...(input.reason.trim() === "" ? {} : { reason: input.reason.trim() }),
        },
      });
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
      return (
        result.data ?? { status: "pending", refundedCents: input.amountCents, remainingCents: 0 }
      );
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["event-registrations", tenantId] });
      await client.invalidateQueries({ queryKey: ["person", tenantId] });
    },
  });
}

function useEventInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (eventId?: string, occurrenceId?: string) => {
    await client.invalidateQueries({ queryKey: ["events", tenantId] });
    await client.invalidateQueries({ queryKey: ["event-categories", tenantId] });
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    if (eventId !== undefined)
      await client.invalidateQueries({ queryKey: ["event-occurrences", tenantId, eventId] });
    if (occurrenceId !== undefined)
      await client.invalidateQueries({ queryKey: ["event-registrations", tenantId, occurrenceId] });
  };
}

export interface EventFields {
  readonly title: string;
  readonly slug: string;
  readonly description: string;
  readonly categoryId: string | null;
  readonly campusId: string | null;
  readonly ministry: string;
  readonly location: string;
  readonly visibility: EventVisibility;
  readonly registration: RegistrationRule;
  readonly capacity: number | null;
  readonly priceCents: number | null;
}

export function useSaveEvent() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: { eventId: string | null; fields: EventFields }) => {
      const f = input.fields;
      const { data, error } = await supabase.rpc("set_event", {
        p_tenant_id: tenantId ?? "",
        p_title: f.title,
        ...(input.eventId === null ? {} : { p_event_id: input.eventId }),
        ...(f.slug.trim() === "" ? {} : { p_slug: f.slug.trim() }),
        ...(f.description.trim() === "" ? {} : { p_description: f.description }),
        ...(f.categoryId === null ? {} : { p_category_id: f.categoryId }),
        ...(f.campusId === null ? {} : { p_campus_id: f.campusId }),
        ...(f.ministry.trim() === "" ? {} : { p_ministry: f.ministry }),
        ...(f.location.trim() === "" ? {} : { p_location: f.location }),
        p_visibility: f.visibility,
        p_registration: f.registration,
        ...(f.capacity === null ? {} : { p_capacity: f.capacity }),
        p_price_cents: f.priceCents ?? 0,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_id, input) => invalidate(input.eventId ?? undefined),
  });
}

export function useSetEventActive() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: { eventId: string; isActive: boolean }) => {
      const { error } = await supabase.rpc("set_event_active", {
        p_event_id: input.eventId,
        p_is_active: input.isActive,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.eventId),
  });
}

export interface RunFields {
  /** ISO local date-time from the input, "2026-12-24T17:00". */
  readonly startsAt: string;
  readonly minutes: number;
  readonly everyDays: number | null;
  /** ISO date, "2027-03-01"; ignored when everyDays is null. */
  readonly until: string;
  readonly note: string;
}

export function useAddOccurrences() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: { eventId: string; fields: RunFields }) => {
      const f = input.fields;
      const { data, error } = await supabase.rpc("add_event_occurrences", {
        p_event_id: input.eventId,
        p_starts_at: new Date(f.startsAt).toISOString(),
        p_duration: `${String(f.minutes)} minutes`,
        ...(f.everyDays === null ? {} : { p_every_days: f.everyDays }),
        ...(f.everyDays === null || f.until === ""
          ? {}
          : { p_until: new Date(`${f.until}T23:59:59`).toISOString() }),
        ...(f.note.trim() === "" ? {} : { p_note: f.note }),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_n, input) => invalidate(input.eventId),
  });
}

export function useUpdateOccurrence() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      occurrenceId: string;
      startsAt: string;
      endsAt: string;
      note: string;
      isCancelled: boolean;
    }) => {
      const { error } = await supabase.rpc("update_event_occurrence", {
        p_occurrence_id: input.occurrenceId,
        p_starts_at: input.startsAt,
        p_ends_at: input.endsAt,
        ...(input.note.trim() === "" ? {} : { p_note: input.note }),
        p_is_cancelled: input.isCancelled,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.eventId, input.occurrenceId),
  });
}

export function useRegisterPerson() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: { eventId: string; occurrenceId: string; personId: string }) => {
      const { error } = await supabase.rpc("register_for_event", {
        p_occurrence_id: input.occurrenceId,
        p_person_id: input.personId,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.eventId, input.occurrenceId),
  });
}

export function useSetRegistrationStatus() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      occurrenceId: string;
      registrationId: string;
      status: RegistrationStatus;
    }) => {
      const { error } = await supabase.rpc("set_registration_status", {
        p_registration_id: input.registrationId,
        p_status: input.status,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.eventId, input.occurrenceId),
  });
}

export function useAttachGuest() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      occurrenceId: string;
      registrationId: string;
      personId: string;
    }) => {
      const { error } = await supabase.rpc("attach_guest_registration", {
        p_registration_id: input.registrationId,
        p_person_id: input.personId,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.eventId, input.occurrenceId),
  });
}

export function useRecordAttendance() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      occurrenceId: string;
      personId: string;
      present: boolean;
    }) => {
      const { error } = await supabase.rpc("record_attendance", {
        p_occurrence_id: input.occurrenceId,
        p_person_id: input.personId,
        p_present: input.present,
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.eventId, input.occurrenceId),
  });
}

export function useSaveEventCategory() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (input: { categoryId: string | null; name: string; sortOrder: number }) => {
      const { error } = await supabase.rpc("set_event_category", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        ...(input.categoryId === null ? {} : { p_category_id: input.categoryId }),
        p_sort_order: input.sortOrder,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useDeleteEventCategory() {
  const supabase = useSupabase();
  const invalidate = useEventInvalidation();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await supabase.rpc("delete_event_category", { p_category_id: categoryId });
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

// ------------------------------------------------------------------------------------------------ add-ons

/** The products an event offers as add-ons, in the church's order (ADR 0047 step 3). */
export function useEventAddOns(eventId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["event-add-ons", tenantId, eventId],
    enabled: eventId !== null && tenantId !== null,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("event_add_ons")
        .select("product_id, sort_order")
        .eq("tenant_id", tenantId ?? "")
        .eq("event_id", eventId ?? "")
        .order("sort_order");
      if (error) throw error;
      return data.map((a) => a.product_id);
    },
  });
}

export function useSetEventAddOns() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly eventId: string;
      readonly productIds: readonly string[];
    }) => {
      const { error } = await supabase.rpc("set_event_add_ons", {
        p_event_id: input.eventId,
        p_product_ids: [...input.productIds],
      });
      if (error) throw error;
    },
    onSuccess: async (_data, input) => {
      await client.invalidateQueries({ queryKey: ["event-add-ons", tenantId, input.eventId] });
    },
  });
}
